import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import {
  clearPostLoginDestination,
  getPostLoginDestination,
  isPublicBookingPath,
  setPostLoginDestination,
} from 'helpers/authFlow'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { setTheme } from 'reducers/themeReducer'
import { getUsersClient } from '../../reducers/clientsReducer'
import { getUser } from '../../reducers/usersReducer'

export type JwtData = {
  UserId?: string
  ClientId?: string
  ApplicationTheme: string
  exp: number
}

type AuthData = {
  isAuthenticated: boolean
  isLoading: boolean
  status: 'loading' | 'unauthenticated' | 'authenticated' | 'bootstrap_failed'
  error?: string
  destination?: string
  setDestination: (value?: string) => void
}

type ProvideAuthProps = {
  children: React.ReactNode
}

const authContext = createContext<AuthData>({
  isAuthenticated: false,
  isLoading: true,
  status: 'loading',
  error: undefined,
  destination: undefined,
  setDestination: () => {},
})

function getDest(path: string): string | undefined {
  if (
    path.startsWith('/sso') ||
    path.startsWith('/logout') ||
    isPublicBookingPath(path)
  ) {
    return undefined
  }

  if (path === '/' || path.startsWith('/auth')) {
    return '/admin/bookings/create?new=true'
  } else {
    return path
  }
}

export default function useAuth(originPath?: string): AuthData {
  const data = useContext(authContext)
  const { destination, setDestination, status } = data

  useEffect(() => {
    if (originPath == null) {
      return
    }

    const isAuthEntryRoute =
      originPath === '/' || originPath.startsWith('/auth')
    if (status === 'authenticated' && !isAuthEntryRoute) {
      return
    }

    const dest = getDest(originPath)
    if (destination == null && dest != null) {
      setDestination(dest)
    }
  }, [destination, originPath, setDestination, status])

  return data
}

function useProvideAuth(): AuthData {
  const dispatch = useDispatch()
  const isAuthenticated = useIsAuthenticated()
  const [isLoading, setIsLoading] = useState(true)
  const [status, setStatus] = useState<AuthData['status']>('loading')
  const [error, setError] = useState<string | undefined>()
  const [destinationState, setDestinationState] = useState<string | undefined>(
    () => getPostLoginDestination(),
  )
  const history = useHistory()
  const { instance } = useMsal()
  const setDestination = useCallback((value?: string) => {
    setDestinationState(value)
    setPostLoginDestination(value)
  }, [])
  const redirectToReauth = useCallback(() => {
    if (typeof window === 'undefined') return
    const path = window.location.pathname || '/'
    if (
      path === '/sso' ||
      path.startsWith('/auth') ||
      path.startsWith('/logout') ||
      isPublicBookingPath(path)
    )
      return

    const currentLocation = `${window.location.pathname}${window.location.search}`
    const target = new URL('/sso', window.location.origin)
    target.searchParams.set('logout_redirect', currentLocation)
    target.searchParams.set('return_to', currentLocation)
    target.searchParams.set('reason', 'bootstrap_auth_failed')
    window.location.replace(target.toString())
  }, [])

  useEffect(() => {
    if (destinationState && !isLoading && isAuthenticated) {
      history.push(destinationState)
      clearPostLoginDestination()
      setDestinationState(undefined)
    }
  }, [destinationState, history, isAuthenticated, isLoading])

  useEffect(() => {
    if (!isAuthenticated) {
      setStatus('unauthenticated')
      setError(undefined)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setStatus('loading')
    setError(undefined)
    dispatch(getUser()).then((result: any) => {
      if (result.error) {
        setStatus('bootstrap_failed')
        setError(result.error?.message || 'Failed to load authenticated user')
        setIsLoading(false)
        redirectToReauth()
      } else {
        Promise.all([
          // dispatch(getUserDashboards(decoded?.UserId)),
          dispatch(getUsersClient()),
          dispatch(
            setTheme(
              instance.getActiveAccount()?.idTokenClaims?.[
                'extension_Jyrney_applicationTheme'
              ],
            ),
          ),
        ]).finally(() => {
          setStatus('authenticated')
          setIsLoading(false) // always set loading to false on the promise chain
        })
      }
    })
  }, [isAuthenticated, dispatch, instance, redirectToReauth])

  return {
    isLoading,
    isAuthenticated,
    status,
    error,
    destination: destinationState,
    setDestination,
  }
}

export function ProvideAuth({ children }: ProvideAuthProps) {
  const auth = useProvideAuth()
  return <authContext.Provider value={auth}>{children}</authContext.Provider>
}
